import * as THREE from 'three';
import { SIM_DT } from '../core/Engine.js';
import { makeRng } from '../core/Rand.js';
import { bestRunway, bestAxisRunway } from './Runway.js';

/**
 * Debug — headless numerical measurement of the movement feel.
 *
 * Everything here is deterministic and only runs when explicitly called
 * (`ctx.player.debug.measure()`), never during boot, gameplay or shots. It
 * temporarily adds test geometry (a staircase) and always removes it again.
 */

const _v = new THREE.Vector3();
const _d = new THREE.Vector3();

const round = (n, p = 3) => Math.round(n * 10 ** p) / 10 ** p;

function basisFwd(yaw, out) {
  return out.set(-Math.sin(yaw), 0, -Math.cos(yaw));
}

/** Pick the yaw with the longest walkable runway from the spawn. */
function clearestYaw(sys, steps = 48) {
  const spawn = sys.movement.spawnFeet(new THREE.Vector3());
  return bestRunway(sys.adapter, sys.cfg, spawn, steps);
}

/** Best axis-aligned yaw with at least `need` metres of walkable runway. */
function clearAxisYaw(sys, need = 10) {
  const spawn = sys.movement.spawnFeet(new THREE.Vector3());
  return bestAxisRunway(sys.adapter, sys.cfg, spawn, need);
}

export function createDebugSuite(sys) {
  const cfg = sys.cfg;
  const mv = sys.movement;

  const home = () => {
    const s = mv.spawnFeet(new THREE.Vector3());
    return s;
  };

  /* ------------------------------------------------------------ metrics */

  function measureJump() {
    const { yaw } = clearestYaw(sys);
    sys.place(home(), yaw, 0);
    sys.run(0.35);
    const y0 = mv.feet.y;
    let apex = y0, tApex = 0, air = 0, trueApex = y0;
    sys._runStep({ jump: true, jumpEdge: true });
    const steps = Math.round(2.0 / SIM_DT);
    for (let i = 0; i < steps; i++) {
      sys._runStep({});
      air += SIM_DT;
      if (mv.feet.y > apex) { apex = mv.feet.y; tApex = air; }
      /* analytic apex of the current ballistic arc — the height the player can
         actually clear, independent of where the 120 Hz samples happen to land */
      if (!mv.grounded && mv.velocity.y > 0) {
        trueApex = Math.max(trueApex, mv.feet.y + (mv.velocity.y ** 2) / (2 * cfg.gravity));
      }
      if (mv.grounded && air > 0.05) break;
    }
    sys._script = null;
    return {
      apexTargetM: cfg.jumpApex,
      apexMeasuredM: round(apex - y0),
      apexBallisticM: round(trueApex - y0),
      timeToApexS: round(tApex),
      airTimeS: round(air),
      impulseMS: round(Math.sqrt(2 * cfg.gravity * cfg.jumpApex)),
    };
  }

  function measureAccel(kind) {
    const { yaw } = clearestYaw(sys);
    sys.place(home(), yaw, 0);
    const input = kind === 'walk' ? { forward: 1 }
      : kind === 'sprint' ? { forward: 1, sprint: true }
        : { forward: 1, sprint: true };
    let tacPrime = kind === 'tac';
    let t = 0, peak = 0, t95 = -1, t99 = -1;
    const steps = Math.round(4.0 / SIM_DT);
    if (tacPrime) {
      /* double-tap sprint: release, press, release, press */
      sys._runStep({ forward: 1, sprint: false });
      sys._runStep({ forward: 1, sprint: true });
      sys._runStep({ forward: 1, sprint: false });
      sys._runStep({ forward: 1, sprint: true });
      t = 4 * SIM_DT;
    }
    const target = kind === 'walk' ? cfg.speedWalk : kind === 'sprint' ? cfg.speedSprint : cfg.speedTacSprint;
    for (let i = 0; i < steps; i++) {
      sys._runStep(input);
      t += SIM_DT;
      peak = Math.max(peak, mv.speed);
      if (t95 < 0 && mv.speed >= target * 0.95) t95 = t;
      if (t99 < 0 && mv.speed >= target * 0.99) t99 = t;
      if (mv.hitWall) break;
    }
    sys._script = null;
    return {
      targetMS: target,
      reachedMS: round(peak),
      timeTo95PctS: round(t95),
      timeTo99PctS: round(t99),
    };
  }

  function measureStop() {
    const { yaw } = clearestYaw(sys);
    sys.place(home(), yaw, 0);
    let steps = Math.round(2.0 / SIM_DT);
    for (let i = 0; i < steps; i++) sys._runStep({ forward: 1 });
    const v0 = mv.speed;
    const p0 = mv.feet.clone();
    let t = 0;
    steps = Math.round(2.0 / SIM_DT);
    for (let i = 0; i < steps; i++) {
      sys._runStep({});
      t += SIM_DT;
      if (mv.speed < 0.02) break;
    }
    sys._script = null;
    return {
      fromSpeedMS: round(v0),
      distanceM: round(Math.hypot(mv.feet.x - p0.x, mv.feet.z - p0.z)),
      timeS: round(t),
    };
  }

  function measureSlide() {
    const c = clearestYaw(sys);
    sys.place(home(), c.yaw, 0);
    let steps = Math.round(2.5 / SIM_DT);
    for (let i = 0; i < steps; i++) {
      sys._runStep({ forward: 1, sprint: true });
      if (mv.speed >= cfg.slideMinSpeed + 0.8) break;
    }
    const entry = mv.speed;
    const p0 = mv.feet.clone();
    sys._runStep({ forward: 1, sprint: true, crouch: true, crouchEdge: true });
    const boosted = mv.speed;
    let t = SIM_DT;
    steps = Math.round(3.0 / SIM_DT);
    let peak = boosted;
    for (let i = 0; i < steps; i++) {
      if (!mv.sliding) break;
      sys._runStep({ forward: 1, crouch: true });
      t += SIM_DT;
      peak = Math.max(peak, mv.speed);
      if (mv.hitWall) break;
    }
    const exit = mv.speed;
    sys._script = null;
    return {
      entrySpeedMS: round(entry),
      boostedSpeedMS: round(mv.slideEntrySpeed || peak),
      distanceM: round(Math.hypot(mv.feet.x - p0.x, mv.feet.z - p0.z)),
      durationS: round(t),
      exitSpeedMS: round(exit),
      runwayM: round(c.dist),
    };
  }

  function measureCrouch() {
    const { yaw } = clearestYaw(sys);
    sys.place(home(), yaw, 0);
    sys.run(0.2);
    const eye0 = sys.rig.eyeHeight;
    let t = 0;
    const steps = Math.round(1.5 / SIM_DT);
    let t90 = -1;
    for (let i = 0; i < steps; i++) {
      sys._runStep({ crouch: true });
      t += SIM_DT;
      const p = (eye0 - sys.rig.eyeHeight) / (cfg.eyeStand - cfg.eyeCrouch);
      if (t90 < 0 && p >= 0.9) t90 = t;
    }
    sys._script = null;
    return {
      eyeStandM: round(eye0),
      eyeCrouchM: round(sys.rig.eyeHeight),
      timeTo90PctS: round(t90),
    };
  }

  /* --------------------------------------------------------- stair test */

  function buildStairs(origin, yaw, rise, run, count, width = 3.2) {
    const g = new THREE.Group();
    g.name = 'player_debug_stairs';
    const along = new THREE.Vector3();
    basisFwd(yaw, along);
    const axisZ = Math.abs(along.z) > Math.abs(along.x);
    const sign = axisZ ? Math.sign(along.z) : Math.sign(along.x);
    const mat = new THREE.MeshBasicMaterial({ color: 0x555555 });
    for (let i = 0; i < count; i++) {
      const h = (i + 1) * rise;
      const d = run;
      const geo = axisZ ? new THREE.BoxGeometry(width, h, d) : new THREE.BoxGeometry(d, h, width);
      const m = new THREE.Mesh(geo, mat);
      const off = (i + 0.5) * run * sign;
      m.position.set(
        origin.x + (axisZ ? 0 : off),
        origin.y + h * 0.5,
        origin.z + (axisZ ? off : 0),
      );
      m.userData.surface = 'concrete';
      g.add(m);
    }
    g.updateMatrixWorld(true);
    return g;
  }

  function withStairs(rise, run, count, fn) {
    const axis = clearAxisYaw(sys, 9);
    if (!axis) return { skipped: 'no clear axis-aligned runway on this level' };
    const spawn = home();
    basisFwd(axis.yaw, _d);
    const origin = spawn.clone().addScaledVector(_d, 3.0);
    origin.y = spawn.y;
    const stairs = buildStairs(origin, axis.yaw, rise, run, count);
    sys.ctx.scene.add(stairs);
    try { sys.ctx.physics?.addStatic?.(stairs); sys.ctx.physics?.rebuild?.(); } catch { /* optional */ }
    sys.adapter.addExtra(stairs);
    let out;
    try {
      out = fn(spawn, axis.yaw, origin);
    } finally {
      try { sys.ctx.physics?.removeStatic?.(stairs); sys.ctx.physics?.rebuild?.(); } catch { /* optional */ }
      sys.adapter.removeExtra(stairs);
      stairs.removeFromParent();
      stairs.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); } });
    }
    return out;
  }

  function measureStairs() {
    const rise = 0.20, run = 0.32, count = 10;
    const climb = withStairs(rise, run, count, (spawn, yaw) => {
      sys.place(spawn, yaw, 0);
      const y0 = mv.feet.y;
      const top = y0 + rise * count;
      const steps = Math.round(5.0 / SIM_DT);
      let peak = y0;
      let stuckFor = 0;
      let t = 0;
      for (let i = 0; i < steps; i++) {
        const before = mv.feet.y;
        sys._runStep({ forward: 1 });
        t += SIM_DT;
        if (mv.feet.y > peak) peak = mv.feet.y;
        if (mv.feet.y - before < 1e-4) stuckFor += SIM_DT; else stuckFor = 0;
        /* stop the moment we are on the top landing, before we walk off it */
        if (peak >= top - 1e-3 && mv.grounded) break;
        if (stuckFor > 1.2) break;
      }
      sys._script = null;
      const climbed = peak - y0;
      return {
        riseM: rise, runM: run, steps: count,
        expectedClimbM: round(rise * count),
        climbedM: round(climbed),
        climbTimeS: round(t),
        success: climbed >= rise * count - 0.02,
      };
    });

    /* a single riser taller than stepHeight must NOT be climbable */
    const tooTall = cfg.stepHeight + 0.12;
    const blocked = withStairs(tooTall, 1.4, 1, (spawn, yaw) => {
      sys.place(spawn, yaw, 0);
      const y0 = mv.feet.y;
      for (let i = 0; i < Math.round(2.5 / SIM_DT); i++) sys._runStep({ forward: 1 });
      sys._script = null;
      return { riserM: round(tooTall), climbedM: round(mv.feet.y - y0), correctlyBlocked: mv.feet.y - y0 < 0.05 };
    });

    return { climb, tallRiser: blocked, stepHeightM: cfg.stepHeight };
  }

  /* ------------------------------------------------------ safety fuzzing */

  function stressTest(seconds = 30, seed = 0x51eed) {
    const rng = makeRng(seed);
    const { yaw } = clearestYaw(sys);
    sys.place(home(), yaw, 0);
    const steps = Math.round(seconds / SIM_DT);
    let worstPenetration = 0;
    let worstMantlePenetration = 0;
    let minY = Infinity;
    let maxSpeed = 0;
    let nonFinite = 0;
    const startCounters = { ...mv.debugCounters };
    let f = 0, r = 0, sprint = false, crouch = false, jump = false, changeIn = 0;
    for (let i = 0; i < steps; i++) {
      if (changeIn <= 0) {
        f = rng() < 0.75 ? (rng() < 0.8 ? 1 : -1) : 0;
        r = rng() < 0.5 ? (rng() < 0.5 ? 1 : -1) : 0;
        sprint = rng() < 0.5;
        crouch = rng() < 0.28;
        jump = rng() < 0.30;
        changeIn = 0.15 + rng() * 0.6;
        sys.rig.yaw += (rng() - 0.5) * 2.2;
      }
      changeIn -= SIM_DT;
      const crouchEdge = crouch && rng() < 0.4;
      sys._runStep({ forward: f, right: r, sprint, crouch, crouchEdge, jump, jumpEdge: jump && rng() < 0.5 });
      const half = mv.halfHeight;
      _v.set(mv.feet.x, mv.feet.y + half, mv.feet.z);
      const pen = sys.adapter.penetration(_v, half, cfg.radius);
      /* A mantle is a scripted arc that deliberately passes through the ledge
         corner (every COD does this) — it is measured separately. What must
         never happen is being left inside geometry once it ends. */
      if (mv.mantling) worstMantlePenetration = Math.max(worstMantlePenetration, pen);
      else worstPenetration = Math.max(worstPenetration, pen);
      minY = Math.min(minY, mv.feet.y);
      maxSpeed = Math.max(maxSpeed, mv.speed);
      if (!Number.isFinite(mv.feet.x + mv.feet.y + mv.feet.z)) nonFinite++;
    }
    sys._script = null;
    return {
      seconds,
      worstPenetrationM: round(worstPenetration, 4),
      worstMantlePenetrationM: round(worstMantlePenetration, 4),
      minFeetY: round(minY),
      levelFloorY: round(sys.adapter.bounds.min.y),
      maxSpeedMS: round(maxSpeed),
      nonFiniteFrames: nonFinite,
      respawns: mv.debugCounters.respawns - startCounters.respawns,
      unstuck: mv.debugCounters.unstuck - startCounters.unstuck,
      jumps: mv.debugCounters.jumps - startCounters.jumps,
      slides: mv.debugCounters.slides - startCounters.slides,
      mantles: mv.debugCounters.mantles - startCounters.mantles,
      neverInsideGeometry: worstPenetration < 0.05,
      neverLeftWorld: minY > sys.adapter.bounds.min.y - cfg.killPlaneMargin,
    };
  }

  /* --------------------------------------------------------------- entry */

  function measure(opts = {}) {
    const wasFrozen = sys.frozen;
    sys.frozen = false;
    const out = {
      physics: sys.adapter.diagnostics,
      jump: measureJump(),
      walk: measureAccel('walk'),
      sprint: measureAccel('sprint'),
      tacSprint: measureAccel('tac'),
      stop: measureStop(),
      slide: measureSlide(),
      crouch: measureCrouch(),
      stairs: measureStairs(),
      safety: stressTest(opts.stressSeconds ?? 30),
      targets: {
        walkMS: cfg.speedWalk, sprintMS: cfg.speedSprint, tacSprintMS: cfg.speedTacSprint,
        crouchMS: cfg.speedCrouch, adsMS: cfg.speedAds,
        jumpApexM: cfg.jumpApex, gravity: round(cfg.gravity),
        slideDurationS: cfg.slideDuration, stepHeightM: cfg.stepHeight,
        eyeStandM: cfg.eyeStand, eyeCrouchM: cfg.eyeCrouch, eyeSlideM: cfg.eyeSlide,
      },
    };
    sys.respawn();
    sys.frozen = wasFrozen;
    return out;
  }

  return {
    measure,
    stressTest,
    clearestYaw: () => clearestYaw(sys),
    physics: () => sys.adapter.diagnostics,
    counters: () => ({ ...sys.movement.debugCounters }),
  };
}

export default createDebugSuite;
